import { useCallback } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { FEATURE_ACCESS, PRODUCT_TYPE_PERMISSIONS } from '../../config/permissions.config';
import type { Action, ApplicationState, ProductType, Role } from '../types/domain';

/**
 * Permission check hook that wraps the permission config lookups.
 *
 * Provides `canPerformAction` and `canAccessFeature` based on the
 * current user's role from the auth store.
 *
 * NOTE: This implements permission checks inline against the config data.
 * When the permission engine (task 3.1) is created, these can be refactored
 * to delegate to the engine's pure functions.
 */
export function usePermission() {
  const role = useAuthStore((state) => state.user?.role ?? null);

  const canPerformAction = useCallback(
    (action: Action, productType: ProductType, applicationState?: ApplicationState): boolean => {
      if (!role) return false;

      const matrix = PRODUCT_TYPE_PERMISSIONS[productType];
      if (!matrix) return false;

      const rolePermissions = matrix[role];
      if (!rolePermissions) return false;

      if (applicationState) {
        const stateActions = rolePermissions[applicationState];
        if (!stateActions) return false;
        return stateActions.includes(action);
      }

      // If no application state provided, check across all states
      return Object.values(rolePermissions).some((actions) =>
        (actions as Action[]).includes(action)
      );
    },
    [role]
  );

  const getAllowedActions = useCallback(
    (productType: ProductType, applicationState?: ApplicationState): Action[] => {
      if (!role) return [];

      const matrix = PRODUCT_TYPE_PERMISSIONS[productType];
      if (!matrix) return [];

      const rolePermissions = matrix[role];
      if (!rolePermissions) return [];

      if (applicationState) {
        return (rolePermissions[applicationState] as Action[] | undefined) ?? [];
      }

      // If no application state, collect all unique actions across states
      const allActions = new Set<Action>();
      for (const actions of Object.values(rolePermissions)) {
        for (const action of actions as Action[]) {
          allActions.add(action);
        }
      }
      return Array.from(allActions);
    },
    [role]
  );

  const canAccessFeature = useCallback(
    (featureId: string): boolean => {
      if (!role) return false;
      const features = FEATURE_ACCESS[role as Role];
      if (!features) return false;
      return features.includes(featureId);
    },
    [role]
  );

  return { canPerformAction, getAllowedActions, canAccessFeature };
}
